import axios from 'axios';
import {
  getAccessToken,
  clearAccessToken,
  getTvUuid,
} from '@/services/auth/authStorage';
import { API_URL, BUILD_NUMBER, MARKETING_VERSION } from '@/constants';
import { Platform } from 'react-native';
import * as Application from 'expo-application';
import * as Device from 'expo-device';
import * as Localization from 'expo-localization';

let onUnauthorized: (() => void) | null = null;

export function setOnUnauthorized(callback: () => void) {
  onUnauthorized = callback;
}

/**
 * Close match to our "device_type" on mobile
 */
const getDeviceType = () => {
  if (Platform.OS === 'web') {
    return 'web';
  }

  const name = Device.modelName ?? '';
  const id = Device.modelId ?? '';

  if (name && id) {
    return `${name} / ${id}`;
  }

  return name || id || null;
};

const getHeaders = () => {
  return {
    'Content-Type': 'application/json',
    version: '3.15.0',
    clientType: 'tv_app',
    devicePlatform: Platform.OS,
    deviceType: getDeviceType(),
    // `nativeBuildVersion` / `nativeApplicationVersion` return null on web.
    // Fall back to the values baked into the JS bundle from app.config.js
    // (.build_number + marketingVersion) so web requests still carry
    // meaningful build/version identifiers.
    buildNumber:
      Application.nativeBuildVersion ?? BUILD_NUMBER?.toString?.() ?? null,
    tvVersion:
      Application.nativeApplicationVersion ?? MARKETING_VERSION ?? null,

    // To keep up with the standard we always had we send the full name + version
    // Note: In the web this is not always reliable.
    // This is extracted by expo from the UserAgent. It is common for browsers to spoof this.
    // Example: On a 26.4 macOS, to show: Mac OS 10_15_7 (Catalina), which is very old
    // And you can confirm this by inspecting the UserAgent on the web
    systemVersion: `${Device.osName} ${Device.osVersion}`,

    timezone: Localization.getCalendars()?.[0]?.timeZone ?? null,
    deviceCountry: Localization.getLocales()?.[0]?.regionCode ?? null,
    ipCountry: null, // TODO

    systemName: Device.osName,
  };
};

const apiClient = axios.create({
  baseURL: API_URL,
  headers: getHeaders(),
});

// Inject token and device id on every request
apiClient.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const tvUuid = getTvUuid();
  if (tvUuid) {
    config.headers['deviceId'] = tvUuid;
  }
  return config;
});

// Clear token and user state on 401/403 auth errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    if (status === 401 || status === 403) {
      console.warn(`Auth error (${status})! Clearing access token.`);
      clearAccessToken();
      onUnauthorized?.();
    }
    return Promise.reject(error);
  },
);

export default apiClient;
